import React, { useMemo } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import cssColorNames from "color-name";

/**
 * Normalise a CSS hex colour to `#rrggbb`, or `null` if not a hex string.
 * Accepts `#rgb`, `#rrggbb`, `#rrggbbaa`, or forms without `#`.
 */
export function parseHexColor(input) {
	if (input == null) return null;
	let s = String(input).trim();
	if (!s) return null;
	if (s.startsWith("#")) s = s.slice(1);
	if (/^[0-9a-f]{3}$/i.test(s)) {
		const a = s[0];
		const b = s[1];
		const c = s[2];
		return `#${a}${a}${b}${b}${c}${c}`.toLowerCase();
	}
	if (/^[0-9a-f]{6}$/i.test(s)) return `#${s.toLowerCase()}`;
	if (/^[0-9a-f]{8}$/i.test(s)) return `#${s.slice(0, 6).toLowerCase()}`;
	return null;
}

/** `color-name` ships RGB tuples; convert to `#rrggbb`. */
function rgbTupleToHex(rgb) {
	if (!Array.isArray(rgb) || rgb.length < 3) return null;
	const r = Math.max(0, Math.min(255, Math.round(Number(rgb[0]))));
	const g = Math.max(0, Math.min(255, Math.round(Number(rgb[1]))));
	const b = Math.max(0, Math.min(255, Math.round(Number(rgb[2]))));
	return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/** Longest keys first so e.g. `darkseagreen` wins over `green`. */
const CSS_COLOR_KEYS_BY_LENGTH = Object.keys(cssColorNames).sort((a, b) => b.length - a.length);

/**
 * Brand / assortment labels not in W3C `color-name` (key = lowercase alphanumeric compact).
 * Checked before the standard named-colour list.
 */
const EXTRA_BRAND_COLOR_HEX = {
	mustard: "#c9a227",
	multi: "#78909c",
	fawn: "#b8956a",
	petrol: "#1a5f6a",
	rust: "#b7410e",
	/** Assortment “off white” — distinct from W3C `white` / substring false matches */
	offwhite: "#faf9f6",
};

function colorNameLookupVariants(raw) {
	const s = String(raw || "").trim().toLowerCase();
	if (!s) return [];
	const compact = s.replace(/[^a-z0-9]/g, "");
	const joined = s.split(/\s+/).filter(Boolean).join("");
	const greyToGray = compact.replace(/grey/g, "gray");
	const grayToGrey = compact.replace(/gray/g, "grey");
	return [...new Set([compact, joined, greyToGray, grayToGrey].filter(Boolean))];
}

function extraBrandHexFromRaw(raw) {
	for (const variant of colorNameLookupVariants(raw)) {
		const hit = EXTRA_BRAND_COLOR_HEX[variant];
		if (hit) return hit;
	}
	const compact = String(raw || "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]/g, "");
	return EXTRA_BRAND_COLOR_HEX[compact] || null;
}

/**
 * Resolve a colour label to a CSS hex: extra brand names first, then W3C `color-name`, then substring match.
 */
export function colorNameToCssHex(raw) {
	const brandHex = extraBrandHexFromRaw(raw);
	if (brandHex) return brandHex;
	for (const variant of colorNameLookupVariants(raw)) {
		const rgb = cssColorNames[variant];
		if (rgb) return rgbTupleToHex(rgb);
	}
	const k = String(raw || "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]/g, "");
	if (!k) return null;
	for (const key of CSS_COLOR_KEYS_BY_LENGTH) {
		if (key.length < 3) continue;
		if (k.includes(key) || key.includes(k)) {
			return rgbTupleToHex(cssColorNames[key]);
		}
	}
	return null;
}

/** Read hex from common product fields, then `color` as hex or W3C name via `color-name`. */
export function getProductColorHex(product) {
	if (!product || typeof product !== "object") return null;
	const keys = [
		"color_hex",
		"colour_hex",
		"colorHex",
		"ColourHex",
		"hex",
		"HEX",
		"html_color",
		"htmlColour",
		"HTML_COLOR",
		"rgb_hex",
	];
	for (const k of keys) {
		const v = product[k];
		if (v != null && String(v).trim() !== "") {
			const h = parseHexColor(v);
			if (h) return h;
		}
	}
	const c = product.color;
	const fromHex = parseHexColor(c);
	if (fromHex) return fromHex;
	return colorNameToCssHex(c);
}

function normalizeKeyForHash(s) {
	return String(s || "Unknown")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]/g, "");
}

export function swatchColorFromName(raw) {
	const hex = parseHexColor(raw);
	if (hex) return hex;
	const named = colorNameToCssHex(raw);
	if (named) return named;
	let h = 0;
	const k = normalizeKeyForHash(raw);
	for (let i = 0; i < k.length; i++) h = ((h << 5) - h + k.charCodeAt(i)) | 0;
	h = Math.abs(h) % 360;
	return `hsl(${h}, 42%, 46%)`;
}

/** Bar fill: explicit `row.hex` first, else name / hex string via `swatchColorFromName`. */
export function swatchFillForContributionRow(row) {
	const fromRow = row?.hex != null ? parseHexColor(row.hex) : null;
	if (fromRow) return fromRow;
	return swatchColorFromName(row?.color);
}

/**
 * Ramp / grid bucket key: **assortment colour label** (trimmed, lower, collapsed spaces).
 * Not resolved CSS hex — otherwise "Off White" can match `white` via substring lookup and merge with "White".
 */
export function colorContributionRampBucketKey(source) {
	if (!source || typeof source !== "object") return "unknown";
	const colorName = String(source.color ?? "Unknown").trim() || "Unknown";
	return colorName.replace(/\s+/g, " ").toLowerCase();
}

/** Same as `colorContributionRampBucketKey` — segment key for drilldown / selection. */
export function colorContributionRowMapKey(row) {
	return colorContributionRampBucketKey(row);
}

function segmentAmount(r) {
	const n = r.amount ?? r.soldQty ?? 0;
	return typeof n === "number" ? n : parseFloat(n) || 0;
}

/** Shallow-clone rows; every colour keeps its own segment (no synthetic “Other” bucket). */
function rowsForRamp(rows) {
	if (!rows || rows.length === 0) return [];
	return rows.map((r) => ({ ...r }));
}

function formatLabelPct(p) {
	return p % 1 === 0 ? `${Math.round(p)}` : p.toFixed(1);
}

function formatPcs(n) {
	return Math.round(Number(n) || 0).toLocaleString();
}

/** Format a share percentage (e.g. % of total GP). */
function formatGpSharePct(num) {
	const value = Number(num);
	if (Number.isNaN(value)) return "0";
	const rounded = Math.round(value * 10) / 10;
	return rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(1);
}

function sohSplitTooltipLines(row, unitLabel) {
	const total = segmentAmount(row);
	const wh = row.whSoh != null ? Math.round(Number(row.whSoh) || 0) : null;
	const ebo = row.eboSoh != null ? Math.round(Number(row.eboSoh) || 0) : null;
	if (wh == null && ebo == null) return null;
	const w = wh ?? 0;
	const e = ebo ?? 0;
	const whPct = total > 0 ? ((w / total) * 100).toFixed(1) : "0.0";
	const eboPct = total > 0 ? ((e / total) * 100).toFixed(1) : "0.0";
	return [
		`WH SOH: ${formatPcs(w)} ${unitLabel} (${whPct}% of this colour)`,
		`EBO SOH: ${formatPcs(e)} ${unitLabel} (${eboPct}% of this colour)`,
	];
}

/**
 * One horizontal row per metric: proportional segments fill 100% width (no horizontal scroll; tooltips carry full detail).
 */
export default function ColorContributionRamp({
	rows,
	title = "COLOR CONTRIBUTION",
	/** Tooltip: "% of {amountLabel} · N {unitLabel}" */
	amountLabel = "sold qty",
	unitLabel = "pcs",
	variant = "light",
	barHeight = 32,
	/** When true, tooltip lists WH SOH vs EBO SOH per colour (rows from aggregateColorSohContributions). */
	sohWarehouseSplit = false,
	/** Click a segment to filter the grid to that colour (parent toggles same key to clear). */
	onSegmentClick,
	/** Highlights the active segment when it matches `colorContributionRowMapKey(row)`. */
	selectedFilterKey = null,
	/** Segment contribution % under the bar (`gpSharePct` on each row; bar width stays from primary metric). */
	showSegmentGp = false,
	/** @deprecated use showSegmentGp — alias kept for sold-qty ramp */
	showSegmentContribution = false,
	/** Label under segment % — e.g. "GP" (sold) or "COGS" (T.INW buy value). */
	contributionLabel = "GP Contri",
}) {
	const showContribution = showSegmentGp || showSegmentContribution;
	const segments = useMemo(() => rowsForRamp(rows || []), [rows]);

	const totalQty = useMemo(
		() => (rows || []).reduce((s, r) => s + segmentAmount(r), 0),
		[rows]
	);

	if (!segments.length) return null;

	const labelColor = variant === "dark" ? "rgba(255,255,255,0.88)" : "#424242";
	const titleColor = variant === "dark" ? "rgba(255,255,255,0.72)" : "#616161";
	const segmentDivider =
		variant === "dark" ? "1px solid rgba(0,0,0,0.22)" : "1px solid rgba(0,0,0,0.06)";
	const titleFontSize = variant === "dark" ? "0.65rem" : "0.6rem";
	const captionFontSize = variant === "dark" ? "0.58rem" : "0.55rem";

	return (
		<Box sx={{ width: "100%" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
					gap: 1,
					mb: 0.75,
					flexWrap: "wrap",
				}}>
				<Typography
					variant="caption"
					component="div"
					sx={{
						color: titleColor,
						fontWeight: 600,
						letterSpacing: "0.07em",
						fontSize: titleFontSize,
						textTransform: "uppercase",
						flex: "1 1 auto",
						minWidth: 0,
					}}>
					{title}
				</Typography>
				<Typography
					variant="caption"
					component="div"
					sx={{
						color: titleColor,
						fontWeight: 600,
						fontSize: titleFontSize,
						letterSpacing: "0.02em",
						textTransform: "none",
						flexShrink: 0,
						opacity: variant === "dark" ? 0.95 : 0.88,
						whiteSpace: "nowrap",
					}}>
					{formatPcs(totalQty)} {unitLabel}
				</Typography>
			</Box>

			<Box
				sx={{
					width: "100%",
					overflow: "hidden",
					pb: 0.25,
				}}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						alignItems: "stretch",
						flexWrap: "nowrap",
						width: "100%",
						minWidth: 0,
						borderRadius: "4px",
						overflow: "hidden",
						boxShadow:
							variant === "light"
								? "inset 0 0 0 1px rgba(0,0,0,0.07)"
								: "inset 0 0 0 1px rgba(255,255,255,0.1)",
					}}>
					{segments.map((row, i) => {
						const rowKey = colorContributionRowMapKey(row);
						const isSelected = selectedFilterKey != null && selectedFilterKey === rowKey;
						const splitLines = sohWarehouseSplit ? sohSplitTooltipLines(row, unitLabel) : null;
						const hexDisp = row.hex ? parseHexColor(row.hex) : null;
						const gpSuffix =
							showContribution && row != null && typeof row === "object" && "gpSharePct" in row
								? ` · ${formatGpSharePct(row.gpSharePct)}% of total ${contributionLabel}`
								: "";
						const mainLine = `${row.color}: ${row.percentage.toFixed(1)}% of ${amountLabel} · ${Math.round(segmentAmount(row))} ${unitLabel}${gpSuffix}${hexDisp ? ` · ${hexDisp}` : ""}${onSegmentClick ? " · Click to filter grid" : ""}`;
						const tooltipTitle =
							splitLines && splitLines.length > 0 ? (
								<Box component="span" sx={{ display: "block", textAlign: "left" }}>
									<Box component="span" sx={{ display: "block", fontWeight: 600 }}>
										{mainLine}
									</Box>
									{splitLines.map((line, li) => (
										<Box
											key={`soh-split-${li}`}
											component="span"
											sx={{ display: "block", mt: 0.5, fontWeight: 400, opacity: 0.95 }}>
											{line}
										</Box>
									))}
								</Box>
							) : (
								mainLine
							);
						return (
							<Tooltip key={`${hexDisp ?? row.color}-${i}`} title={tooltipTitle} arrow>
								<Box
									onClick={
										onSegmentClick
											? (e) => {
													e.stopPropagation();
													onSegmentClick(row);
												}
											: undefined
									}
									role={onSegmentClick ? "button" : undefined}
									tabIndex={onSegmentClick ? 0 : undefined}
									onKeyDown={
										onSegmentClick
											? (e) => {
													if (e.key === "Enter" || e.key === " ") {
														e.preventDefault();
														onSegmentClick(row);
													}
												}
											: undefined
									}
									sx={{
										flex: `0 1 ${row.percentage}%`,
										minWidth: 0,
										display: "flex",
										flexDirection: "column",
										alignItems: "stretch",
										cursor: onSegmentClick ? "pointer" : "default",
										borderRadius: isSelected ? "4px" : 0,
										outline: isSelected ? (variant === "dark" ? "2px solid #81c784" : "2px solid #2e7d32") : "none",
										outlineOffset: isSelected ? 2 : 0,
									}}>
									<Box
										sx={{
											height: barHeight,
											flexShrink: 0,
											backgroundColor: swatchFillForContributionRow(row),
											borderRight: i < segments.length - 1 ? segmentDivider : "none",
										}}
									/>
									<Box sx={{ mt: 0.5, width: "100%", minHeight: 0 }}>
										<Typography
											variant="caption"
											component="div"
											title={`${row.color} · ${formatLabelPct(row.percentage)}%${hexDisp ? ` · ${hexDisp}` : ""}`}
											sx={{
												px: 0.125,
												textAlign: "center",
												color: labelColor,
												fontSize: captionFontSize,
												fontWeight: 600,
												lineHeight: 1.2,
												width: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}>
											{formatLabelPct(row.percentage)}%
										</Typography>
										{showContribution && row != null && typeof row === "object" && "gpSharePct" in row ? (
											<Typography
												variant="caption"
												component="div"
												sx={{
													px: 0.125,
													textAlign: "center",
													color: titleColor,
													fontSize: variant === "dark" ? "0.5rem" : "0.48rem",
													fontWeight: 600,
													lineHeight: 1.15,
													width: "100%",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													opacity: variant === "dark" ? 0.9 : 0.82,
												}}>
												{formatGpSharePct(row.gpSharePct)}% {contributionLabel}
											</Typography>
										) : null}
									</Box>
								</Box>
							</Tooltip>
						);
					})}
				</Box>
			</Box>
		</Box>
	);
}
